import type { ControlsPlacement } from '@workwave-tidal/sandbox-utils'
import clsx from 'clsx'
import { type MotionNodeDragHandlers, type MotionValue, motion, useMotionValue } from 'framer-motion'
import { type ReactNode, useCallback, useEffect } from 'react'
import classes from './resizable-panel.module.css'

interface Props {
  initialSize?: number
  minSize?: number
  children?: ReactNode
  placement: ControlsPlacement
  disabled?: boolean
}

export function ResizablePanel(props: Props) {
  const { children, initialSize, minSize, placement, disabled } = props

  let defaultMinSize = 50
  let defaultSize = 50

  switch (placement) {
    case 'bottom':
      defaultSize = initialSize ?? 300
      defaultMinSize = minSize ?? 50
      break

    case 'top':
      defaultSize = initialSize ?? 100
      defaultMinSize = minSize ?? 50
      break

    case 'right':
      defaultSize = initialSize ?? 300
      defaultMinSize = minSize ?? 150
      break

    case 'left':
      defaultSize = initialSize ?? 50
      defaultMinSize = minSize ?? 50
      break
  }

  const mSize = useMotionValue(defaultSize)
  useEffect(() => {
    if (initialSize !== undefined) {
      mSize.set(initialSize)
    }
  }, [mSize, initialSize])
  const direction = placement === 'top' || placement === 'bottom' ? 'vertical' : 'horizontal'

  if (disabled) {
    return <>{children}</>
  }

  const cls = clsx(classes.resizablePanel, {
    [classes.vertical]: direction === 'vertical',
    [classes.horizontal]: direction === 'horizontal',
  })
  const style = {
    [direction === 'horizontal' ? 'width' : 'height']: mSize,
  }

  return (
    <motion.div className={cls} data-id="resizable-panel" style={style}>
      {children}
      <ResizeHandle minSize={defaultMinSize} mSize={mSize} placement={placement} resetSize={defaultSize} />
    </motion.div>
  )
}

type ResizeHandleProps = {
  resetSize: number
  mSize: MotionValue<number>
  minSize: number
  placement: ControlsPlacement
}

function ResizeHandle(props: ResizeHandleProps) {
  const { mSize, minSize, resetSize, placement } = props

  const direction = placement === 'top' || placement === 'bottom' ? 'vertical' : 'horizontal'

  const handleOnResize = useCallback<Required<MotionNodeDragHandlers>['onDrag']>(
    (_event, info) => {
      const directionModifier =
        direction === 'horizontal' ? (placement === 'right' ? -1 : 1) : placement === 'bottom' ? -1 : 1
      const delta = direction === 'horizontal' ? info.delta.x : info.delta.y
      const newSize = mSize.get() + delta * directionModifier

      if (newSize >= minSize) mSize.set(newSize)
    },
    [direction, mSize, minSize, placement],
  )

  const handleReset = useCallback(() => {
    mSize.set(resetSize)
  }, [mSize, resetSize])

  const handleCls = clsx(classes.resizeHandle, {
    [classes.vertical]: direction === 'vertical',
    [classes.horizontal]: direction === 'horizontal',
    [classes.top]: placement === 'top',
    [classes.bottom]: placement === 'bottom',
    [classes.left]: placement === 'left',
    [classes.right]: placement === 'right',
  })

  const handleInnerCls = clsx(classes.resizeHandleInner, {
    [classes.vertical]: direction === 'vertical',
    [classes.horizontal]: direction === 'horizontal',
    [classes.top]: placement === 'top',
    [classes.bottom]: placement === 'bottom',
    [classes.left]: placement === 'left',
    [classes.right]: placement === 'right',
  })

  return (
    <motion.div
      className={handleCls}
      drag={direction === 'horizontal' ? 'x' : 'y'}
      dragConstraints={{ bottom: 0, left: 0, right: 0, top: 0 }}
      dragElastic={0}
      dragMomentum={false}
      onDrag={handleOnResize}
      whileDrag={{
        backgroundColor: 'rgb(230, 230, 230, 1)',
        transition: { duration: 0.1 },
      }}
      whileHover={{
        backgroundColor: 'rgb(230, 230, 230, 1)',
        transition: { duration: 0.1 },
      }}
    >
      <div className={handleInnerCls} onDoubleClick={handleReset} />
    </motion.div>
  )
}
